/*
 * Copyright (C) Fraunhofer IESE 2021-2025 - Mher Ter-Tovmasyan
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  SLRFormInfo,
  SLRTooltip,
  getIcon,
  isEmptyOrNull
} from '@slr/shared-library';
import { FC, useCallback, useEffect } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { OrganizationDraftInput } from '../schema/organizationDraft';

export const MemberForm: FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'draftPage.memberEmailAddresses'
  });

  const {
    formState: { errors },
    control,
    register,
    watch
  } = useFormContext<OrganizationDraftInput>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'emails'
  });

  const handleAddEmail = useCallback(() => {
    append({ email: '' });
  }, [append]);

  const handleDeleteEmail = useCallback(
    (index: number) => remove(index),
    [remove]
  );

  useEffect(() => {
    append({ email: '' }, { shouldFocus: false });
  }, [append]);

  const lastValue = watch(`emails.${fields.length - 1}.email`);

  return (
    <Row>
      <Col xs="12">
        {fields.map((field, index) => (
          <Row key={field.id} className="pb-2">
            <Col>
              <Form.Label
                htmlFor={`emails.${index}.email`}
                className="visually-hidden"
              >
                {t('email')}
              </Form.Label>
              <Form.Control
                key={field.id}
                type="text"
                className="form-control-lg"
                placeholder={t('placeholder')}
                id={`emails.${index}.email`}
                isInvalid={!!errors.emails?.[index]}
                {...register(`emails.${index}.email`)}
              />
              <SLRFormInfo
                isInvalid={!!errors.emails?.[index]?.email}
                text={errors.emails?.[index]?.email?.message}
              />
            </Col>
            <Col xs="auto">
              <SLRTooltip
                content={<span>{t('remove')}</span>}
                trigger={['hover', 'focus']}
                placement="auto"
              >
                <Button
                  variant="link"
                  size="lg"
                  title={t('remove')}
                  onClick={() => handleDeleteEmail(index)}
                  className="px-0"
                >
                  <FontAwesomeIcon
                    icon={getIcon('fas', 'minus')}
                    width={25}
                    className="text-danger cursor-pointer"
                    size="lg"
                  />
                </Button>
              </SLRTooltip>
            </Col>
          </Row>
        ))}
      </Col>
      {(!isEmptyOrNull(lastValue) || fields.length === 0) && (
        <Col xs="12">
          <Button
            variant="link"
            size="lg"
            onClick={handleAddEmail}
            className="px-0"
          >
            {t(fields.length === 0 ? 'add' : 'addMore')}
          </Button>
        </Col>
      )}
    </Row>
  );
};
